import React, { useState } from 'react';
import { Alert, Button, Spinner } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n';

/**
 * «تغییر خدمت» — re-point a live appointment at another medical service.
 *
 * docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §S13. The one place the
 * encounter's service changes after booking, and it changes the APPOINTMENT:
 * the invoice follows, never the other way round. That is why this dialog is
 * reached from the primary line and not from a line editor — editing the line
 * would be editing the consequence.
 *
 * A reason is mandatory once money has been taken: at that point this is a
 * judgement about somebody's bill rather than a correction of a typo, and it
 * carries the same obligation to say why as every other discretionary money act
 * at this desk. The SERVER enforces it; this asks first so the desk is not
 * refused after typing.
 */
export default function ChangeServiceDialog({
  open, current, options = [], hasMoney = false, onCancel, onConfirm,
}) {
  const { t, moneyUnit, formatMoney } = useTranslation();
  const [picked, setPicked] = useState(current || null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (open) { setPicked(current || null); setReason(''); setError(''); }
  }, [open, current]);

  if (!open) return null;

  const changed = !!picked && picked !== current;
  const needsReason = hasMoney && !reason.trim();
  const submit = async () => {
    if (!changed || needsReason) return;
    setSaving(true); setError('');
    try {
      await onConfirm(picked, reason.trim());
    } catch (e) {
      // Reported HERE, inside the open form, and every keystroke survives: a
      // refusal the desk has to retype its answer after is a refusal it will
      // learn to route around.
      setError(e.message || t('cashier.changeService.failed'));
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="change-service-dialog"
      style={{
        marginBottom: 14, padding: '12px 14px', borderRadius: 10,
        background: '#fff', border: `1px solid ${C.navy}`,
      }}
    >
      <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>
        {t('cashier.changeService.title')}
      </div>
      <div style={{ fontSize: '0.71875rem', color: C.textMuted, lineHeight: 1.7, marginBottom: 10 }}>
        {t('cashier.changeService.hint')}
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      {options.length === 0 ? (
        <div style={{ fontSize: '0.75rem', color: C.textMuted }}>
          {t('cashier.changeService.noOptions')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
          {options.map((svc) => {
            const on = picked === svc.declarationId;
            return (
              <button
                key={svc.declarationId}
                type="button"
                onClick={() => setPicked(svc.declarationId)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  textAlign: 'start', fontFamily: 'inherit', fontSize: '0.78125rem',
                  border: `1px solid ${on ? C.navy : C.border}`,
                  background: on ? C.navyLight || '#EFF6FF' : '#fff',
                  color: C.textPrimary,
                }}
              >
                <span style={{ fontWeight: on ? 700 : 500 }}>
                  {svc.label}
                  {svc.declarationId === current ? (
                    <span style={{ color: C.textMuted, fontWeight: 400 }}>
                      {' · '}{t('cashier.changeService.current')}
                    </span>
                  ) : null}
                </span>
                <span style={{ color: C.textMuted, flexShrink: 0 }}>
                  {/* Null, never «۰ ت»: a service the clinic has not priced is
                      not a free one, and the desk must be able to see that
                      before it re-points the bill at it. */}
                  {svc.unitPriceRial
                    ? `${formatMoney(svc.unitPriceRial, { currency: svc?.currency })} ${moneyUnit('short', svc?.currency)}`
                    : t('cashier.line.unpriced')}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {hasMoney ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginBottom: 4 }}>
            {t('cashier.changeService.reasonRequired')}
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('cashier.changeService.reasonPlaceholder')}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 10px',
              border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.78125rem',
              fontFamily: 'inherit',
            }}
          />
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          {t('common.actions.cancel')}
        </Button>
        <Button
          onClick={submit}
          disabled={!changed || needsReason || saving}
          data-testid="change-service-submit"
        >
          {saving ? <Spinner size={14} /> : t('cashier.changeService.submit')}
        </Button>
      </div>
    </div>
  );
}
